import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Clock, Video, Image, 
  MessageSquare, X, Loader2, Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const CONTENT_TYPE_ICONS = {
  video: Video,
  image: Image,
  text: MessageSquare
};

const PLATFORM_COLORS = {
  tiktok: 'bg-pink-500/20 text-pink-400',
  instagram: 'bg-purple-500/20 text-purple-400',
  linkedin: 'bg-blue-500/20 text-blue-400',
  twitter: 'bg-sky-500/20 text-sky-400',
  youtube: 'bg-red-500/20 text-red-400',
  facebook: 'bg-blue-600/20 text-blue-400'
};

/**
 * CalendarPanel Component
 * Content calendar with month/week views and scheduling
 */
export default function CalendarPanel() {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // month or week
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    content_type: 'video',
    platforms: [],
    scheduled_at: '',
    caption: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [currentDate]);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await axios.get(`${API}/calendar/month/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getPostsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return posts.filter(post => {
      const postDate = new Date(post.scheduled_at).toISOString().split('T')[0];
      return postDate === dateStr;
    });
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const scheduled = new Date(date);
    scheduled.setHours(12, 0, 0, 0);
    setNewPost(prev => ({
      ...prev,
      scheduled_at: scheduled.toISOString().slice(0, 16)
    }));
  };

  const togglePlatform = (platform) => {
    setNewPost(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.platforms.length || !newPost.scheduled_at) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/calendar/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post scheduled!');
      setShowAddModal(false);
      setNewPost({ title: '', content_type: 'video', platforms: [], scheduled_at: '', caption: '' });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to schedule post');
    } finally {
      setCreating(false);
    }
  };

  const deletePost = async (postId) => {
    if (!confirm('Delete this scheduled post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/calendar/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/5">
        <CardContent className="p-6 flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h3 className="text-lg font-semibold min-w-40 text-center">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button size="sm" onClick={() => setShowAddModal(true)} data-testid="schedule-post-btn">
          <Plus className="w-4 h-4 mr-1" />
          Schedule Post
        </Button>
      </div>

      {/* Calendar Grid */}
      <Card className="glass-card border-white/5 overflow-hidden">
        <CardContent className="p-0">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-white/10">
            {DAYS.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {getDaysInMonth().map((date, index) => {
              const dayPosts = getPostsForDate(date);
              const today = isToday(date);
              const past = isPast(date);
              
              return (
                <div
                  key={index}
                  className={`
                    min-h-24 p-1 border-b border-r border-white/5 transition-colors cursor-pointer
                    ${!date ? 'bg-white/2' : 'hover:bg-white/5'}
                    ${today ? 'bg-primary/10' : ''}
                    ${past ? 'opacity-50' : ''}
                  `}
                  onClick={() => date && !past && handleDateClick(date)}
                >
                  {date && (
                    <>
                      <div className={`text-xs font-medium mb-1 ${today ? 'text-primary' : ''}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {dayPosts.slice(0, 3).map((post) => {
                          const Icon = CONTENT_TYPE_ICONS[post.content_type] || MessageSquare;
                          return (
                            <div
                              key={post.id}
                              className="text-xs p-1 rounded bg-white/10 truncate flex items-center gap-1 group"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate flex-1">{post.title}</span>
                              <button
                                onClick={() => deletePost(post.id)}
                                className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                        {dayPosts.length > 3 && (
                          <div className="text-xs text-muted-foreground">
                            +{dayPosts.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Scheduled', value: posts.filter(p => p.status === 'scheduled').length, color: 'text-blue-400' },
          { label: 'Published', value: posts.filter(p => p.status === 'published').length, color: 'text-green-400' },
          { label: 'Failed', value: posts.filter(p => p.status === 'failed').length, color: 'text-red-400' }
        ].map((stat) => (
          <Card key={stat.label} className="glass-card border-white/5">
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Post Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass-card rounded-xl p-6 w-full max-w-md border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Schedule Post</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground">Title *</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                    value={newPost.title}
                    onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                    placeholder="Summer Collection Launch"
                    data-testid="post-title-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Content Type</label>
                  <div className="flex gap-2 mt-1">
                    {['video', 'image', 'text'].map((type) => {
                      const Icon = CONTENT_TYPE_ICONS[type];
                      return (
                        <Button
                          key={type}
                          variant={newPost.content_type === type ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewPost({...newPost, content_type: type})}
                        >
                          <Icon className="w-4 h-4 mr-1" />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Platforms *</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.keys(PLATFORM_COLORS).map((platform) => (
                      <Badge
                        key={platform}
                        variant={newPost.platforms.includes(platform) ? 'default' : 'outline'}
                        className="cursor-pointer capitalize"
                        onClick={() => togglePlatform(platform)}
                      >
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Schedule Time *</label>
                  <input
                    type="datetime-local"
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                    value={newPost.scheduled_at}
                    onChange={(e) => setNewPost({...newPost, scheduled_at: e.target.value})}
                    data-testid="post-datetime-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Caption</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none resize-none"
                    rows={3}
                    value={newPost.caption}
                    onChange={(e) => setNewPost({...newPost, caption: e.target.value})}
                    placeholder="Write your caption here..."
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={createPost} disabled={creating} data-testid="create-post-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
